import { type AggResponse, fetchAgg } from '../../../common/utils/fetch-agg';
import { inMemoryCache } from '../../../utils/caching/in-memory';

import type {
	JiraIntentDetectionQueryVariables,
	KnowledgeDiscoveryIntentDetectionResponse,
	KnowledgeDiscoveryJiraIntentDetection,
	KnowledgeDiscoveryJiraIntentDetectionResult,
	QueryError,
} from './types';

const GQL_KNOWLEDGE_DISCOVERY_INTENT_OPERATION_NAME = 'KnowledgeDiscoveryJiraIntentDetectionQuery';

/**
 * GraphQL query string for Jira intent detection.
 */
const jiraIntentDetectionQueryString = `query ${GQL_KNOWLEDGE_DISCOVERY_INTENT_OPERATION_NAME}(
	$siteId: String!
	$orgId: String!
	$query: String!
	$experience: KnowledgeDiscoveryJiraSearchExperience!
	$model: KnowledgeDiscoveryJiraIntentDetectionModel!
	$sessionId: String
	$includePatternType: Boolean!
) {
	knowledgeDiscovery {
		jiraIntentDetection(
			siteId: $siteId
			orgId: $orgId
			query: $query
			experience: $experience
			model: $model
			sessionId: $sessionId
		) @optIn(to: "KnowledgeDiscovery Jira Intent Detection") {
			__typename
			... on KnowledgeDiscoveryJiraIntentDetection {
				searchClassification {
					type
					pattern @include(if: $includePatternType)
				}
			}
			... on QueryError {
				message
			}
		}
	}
}`;

/**
 * In-memory cache for intent detection results.
 * Cache key format: "siteId:orgId:query:experience[:model]".
 */
export const intentDetectionCache: {
	get: (key: string) => Promise<AggResponse<KnowledgeDiscoveryIntentDetectionResponse>> | undefined;
	set: (
		key: string,
		result: Promise<AggResponse<KnowledgeDiscoveryIntentDetectionResponse>>,
	) => void;
	has: (key: string) => boolean;
	clear: (key?: string) => void;
	inMemoryDecorator: (
		key: string,
		supplier: () => Promise<AggResponse<KnowledgeDiscoveryIntentDetectionResponse>>,
		expireWhen?:
			| ((result: AggResponse<KnowledgeDiscoveryIntentDetectionResponse>) => boolean)
			| undefined,
	) => Promise<AggResponse<KnowledgeDiscoveryIntentDetectionResponse>>;
} = inMemoryCache<AggResponse<KnowledgeDiscoveryIntentDetectionResponse>>();

/**
 * Query the Knowledge Discovery API for Jira search intent detection.
 *
 * @param variables - Query variables including siteId, orgId, query, experience, and optional model.
 * @returns Promise resolving to the intent detection result.
 * @throws Promise rejection with GraphQL errors if the query fails.
 */
export const jiraIntentDetectionQuery: (
	variables: JiraIntentDetectionQueryVariables,
	headers?: Record<string, string>,
) => Promise<AggResponse<KnowledgeDiscoveryIntentDetectionResponse>> = async (
	variables: JiraIntentDetectionQueryVariables,
	headers?: Record<string, string>,
): Promise<AggResponse<KnowledgeDiscoveryIntentDetectionResponse>> => {
	const cacheKey = createCacheKey(variables);

	return intentDetectionCache.inMemoryDecorator(cacheKey, async () => {
		const response = await fetchAgg<
			JiraIntentDetectionQueryVariables,
			KnowledgeDiscoveryIntentDetectionResponse
		>({
			graphQuery: jiraIntentDetectionQueryString,
			variables,
			operationName: GQL_KNOWLEDGE_DISCOVERY_INTENT_OPERATION_NAME,
			headers: {
				'X-ExperimentalApi': 'KnowledgeDiscovery',
				...headers,
			},
		});

		const hasGraphQLErrors = response.errors?.some((error) => error);

		if (hasGraphQLErrors) {
			return Promise.reject(response);
		}

		const result = response.data?.knowledgeDiscovery.jiraIntentDetection;
		if (result?.__typename === 'QueryError') {
			return Promise.reject(response);
		}

		return response;
	});
};

/**
 * Helper function to create consistent cache keys.
 */
export const createCacheKey: (variables: JiraIntentDetectionQueryVariables) => string = (
	variables: JiraIntentDetectionQueryVariables,
): string => {
	const { siteId, orgId, query, experience, model, includePatternType } = variables;
	const parts = [siteId, orgId, query, experience];
	if (model) {
		parts.push(model);
	}
	parts.push(`includePatternType=${includePatternType}`);
	return parts.join(':');
};

/**
 * Type guard to check if result is successful intent detection.
 */
export function isIntentDetectionSuccess(
	result: KnowledgeDiscoveryJiraIntentDetectionResult | undefined | null,
): result is {
	__typename: 'KnowledgeDiscoveryJiraIntentDetection';
} & KnowledgeDiscoveryJiraIntentDetection {
	return result?.__typename === 'KnowledgeDiscoveryJiraIntentDetection';
}

/**
 * Type guard to check if result is a query error.
 */
export function isIntentDetectionError(
	result: KnowledgeDiscoveryJiraIntentDetectionResult | undefined | null,
): result is { __typename: 'QueryError' } & QueryError {
	return result?.__typename === 'QueryError';
}
